import { inject, observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { useTranslation } from '../../t';
import withAuth from '../../HOC/withAuth';
import Empty from '../../components/Empty';
import ListSkeleton from '../../components/ListSkeleton';
import defaultPicture from '../../assets/default.jpg';
import { getSpaceUsedInUnit } from '../../util';
import C from '../../colors';
import { getPictureThumbnailUrl } from '../../pictureUrls';

const OrderListDeleted = inject('ordersStore', 'dialogStore', 'userStore', 'snackbarStore')(observer(({ ordersStore, dialogStore, userStore, snackbarStore }) => {
  const { t } = useTranslation();

  useEffect(() => { ordersStore.fetchOrdersDeleted(); }, [ordersStore]);

  const handleRestore = (id) => {
    if (userStore.level === 0 && ordersStore.orders.length >= 1) {
      snackbarStore.showSnackbar('免费账户仅限一个订单，请先删除订单列表中的订单', 'error');
    } else {
      ordersStore.restoreOrder(id);
    }
  };

  const handleDeletePermanent = (id) => {
    dialogStore.openDialog(
      '确定释放此订单的全部图片空间？照片与 OSS 文件将被永久清理，订单、选片及财务历史会保留，但此订单将无法恢复。',
      () => ordersStore.deletePermanent(id)
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <header style={{ padding: '28px 40px 22px', borderBottom: `1px solid ${C.hair}`, background: C.paper, flexShrink: 0 }}>
        <div style={{ fontFamily: C.mono, fontSize: 11, letterSpacing: 1.4, textTransform: 'uppercase', color: C.brand2, fontWeight: 600, marginBottom: 6 }}>
          Workspace · Recycle Bin
        </div>
        <h1 style={{ margin: '0 0 8px', fontFamily: C.serif, fontSize: 38, fontWeight: 400, letterSpacing: -0.5, color: C.ink }}>
          回收站
        </h1>
        <div style={{ padding: '10px 14px', background: C.warningBg, borderRadius: 8, fontSize: 13, color: C.warningFg, display: 'inline-block' }}>
          回收站订单将在 30 天后自动释放图片空间；订单、选片及财务历史会保留。资源释放后无法恢复。
        </div>
      </header>

      {/* List */}
      <section style={{ flex: 1, overflow: 'auto', padding: '24px 40px' }}>
        {ordersStore.isLoadingDeleted ? (
          <ListSkeleton variant="grid" count={6}/>
        ) : !ordersStore.ordersDeleted?.length ? (
          <Empty message="回收站是空的"/>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {ordersStore.ordersDeleted.map(order => (
              <div key={order.id} data-testid={`deleted-order-card-${order.id}`} style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 12, overflow: 'hidden' }}>
                <div style={{
                  aspectRatio: '4/3',
                  backgroundImage: `url(${getPictureThumbnailUrl(order?.Album?.coverPicture) || defaultPicture})`,
                  backgroundSize: 'cover', backgroundPosition: 'center',
                }}/>
                <div style={{ padding: '12px 14px' }}>
                  <h3 style={{ margin: '0 0 6px', fontFamily: C.serif, fontSize: 18, fontWeight: 400, color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {order.customerName}
                  </h3>
                  <div style={{ fontSize: 12, color: C.ink3, display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 12 }}>
                    {order.package && <span>套餐：{order.package.name}</span>}
                    <span>已选 {order.selectedPhotoCount || 0} / 共 {order.Album?.pictureCount} 张</span>
                    {order.Album?.spaceUsed > 0 && <span>占用 {getSpaceUsedInUnit(order.Album.spaceUsed)}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button data-testid={`deleted-order-restore-${order.id}`} onClick={() => handleRestore(order.id)} style={{
                      flex: 1, padding: '7px 0', borderRadius: 7, border: 'none',
                      background: C.brand, color: '#fff',
                      fontFamily: C.sans, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    }}>
                      恢复
                    </button>
                    <button data-testid={`deleted-order-release-${order.id}`} onClick={() => handleDeletePermanent(order.id)} style={{
                      flex: 1, padding: '7px 0', borderRadius: 7,
                      border: `1px solid ${C.hair2}`, background: '#fff',
                      color: C.dangerFg, fontFamily: C.sans, fontSize: 12, cursor: 'pointer',
                    }}>
                      释放图片
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}));

export default withAuth(OrderListDeleted);
